import { useTranslation } from "react-i18next";

import { useUploadAttachment } from "hooks";
import { isFileWithinSizeLimit } from "utils/common";

import type { ControllerRenderProps, FieldValues, Path } from "react-hook-form";

type UploadImageProps = {
  path: string;
  baseUrl: string;
};

export const useUploadImage = <T extends FieldValues>({ baseUrl, path }: UploadImageProps) => {
  const { t } = useTranslation();

  const { handleUploadFileAttachment } = useUploadAttachment(path, baseUrl);

  const handleChangeFile = async (
    event: React.ChangeEvent<HTMLInputElement>,
    fileFieldName: string,
    field: ControllerRenderProps<T, Path<T>>,
    onSuccess?: (res: string) => void,
    onError?: (res: string) => void
  ) => {
    const uploadedFile = event.target.files?.[0];
    event.target.value = "";
    if (!uploadedFile || !isFileWithinSizeLimit(uploadedFile, t)) return;

    const formData = new FormData();
    formData.append(fileFieldName, uploadedFile);

    try {
      const attachmentUrl = await handleUploadFileAttachment({ formData });
      if (attachmentUrl) {
        field.onChange(attachmentUrl);
        onSuccess?.(attachmentUrl);
      } else throw new Error(t("v8_something_went_wrong", "Something went wrong, please contact the administrator."));
    } catch (e) {
      let errMessage = "";

      if (e instanceof Error) errMessage = e.message;
      else errMessage = t("v8_something_went_wrong", "Something went wrong, please contact the administrator.");

      onError?.(errMessage);
    }
  };

  const handleUpload = (name: string) => {
    document.getElementById(name)?.click();
  };

  const handleDelete = (field: ControllerRenderProps<T, Path<T>>) => {
    field.onChange(null);
  };

  return { handleDelete, handleUpload, handleChangeFile };
};
